import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectsAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Plus, 
  FolderOpen, 
  Calendar, 
  Users, 
  ChevronRight,
  Loader2,
  AlertCircle,
  BarChart3
} from 'lucide-react';

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setError('');
    try {
      const res = await projectsAPI.getAll();
      setProjects(res.data || []);
    } catch (err) {
      // Only show error if it's not a 401 (handled by auth)
      if (err.response?.status !== 401) {
        setError('Failed to load projects. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError('');
    loadProjects();
  };

  const handleDeleteProject = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectsAPI.delete(id);
      setProjects(projects.filter(p => p.id !== id));
    } catch (err) {
      setError('Failed to delete project');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your projects and track execution progress
            </p>
          </div>
          <Link to="/projects/new">
            <Button data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="flex items-center justify-between p-4 bg-destructive/10 text-destructive rounded-lg mb-6" data-testid="dashboard-error">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry} data-testid="retry-btn">
              Try Again
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Empty State - only show if no error */}
        {!loading && !error && projects.length === 0 && (
          <Card className="border-dashed border-2 border-slate-200 bg-white animate-fade-in">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Create your first project to generate AI-powered execution plans
              </p>
              <Link to="/projects/new">
                <Button data-testid="empty-create-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Project
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Projects Grid */}
        {!loading && projects.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <Link 
                key={project.id} 
                to={`/projects/${project.id}`}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`project-card-${project.id}`}
              >
                <Card className="border-slate-200 bg-white hover:border-primary/30 transition-colors h-full group">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="font-heading text-lg truncate group-hover:text-primary transition-colors">
                          {project.name}
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {project.scope.substring(0, 100)}...
                        </CardDescription>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        <span>{project.timeline}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-4 h-4" />
                        <span>{project.teams.length} teams</span>
                      </div>
                      {project.execution_plan && (
                        <div className="flex items-center gap-1.5">
                          <BarChart3 className="w-4 h-4 text-accent" />
                          <span className="text-accent font-medium">
                            {project.execution_plan.confidence_score}%
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {project.execution_plan && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex gap-4 text-xs">
                          <span className="text-muted-foreground">
                            {project.execution_plan.milestones?.length || 0} milestones
                          </span>
                          <span className="text-muted-foreground">
                            {Object.values(project.execution_plan.tasks || {}).flat().length} tasks
                          </span>
                          <span className="text-muted-foreground">
                            {project.execution_plan.blockers?.filter(b => !b.resolved).length || 0} blockers
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!project.execution_plan && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <span className="text-xs text-amber-600 font-medium">
                          Plan not generated
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
